import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Upload, Loader2, Star, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { base44 } from "@/api/base44Client";

const inputStyle = { width: "100%", padding: "11px 14px", border: "1px solid #DDDDDD", borderRadius: 8, fontSize: 14, fontFamily: "'Inter', sans-serif", boxSizing: "border-box", outline: "none" };
const labelStyle = { fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#888", display: "block", marginBottom: 6, fontFamily: "'Inter', sans-serif" };

export default function ListingFormModal({ listing, onClose, onSaved }) {
  const isEdit = Boolean(listing);
  const [form, setForm] = useState({
    name: listing?.name || "",
    location: listing?.location || "",
    price: listing?.price ?? "",
    guests: listing?.guests ?? "",
    dateLaunched: listing?.dateLaunched || "",
    listingUrl: listing?.listingUrl || "",
    featured: listing?.featured || false,
  });
  const [images, setImages] = useState(listing?.images || []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const p = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = p; };
  }, []);

  const setF = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = 5 - images.length;
    const toDo = files.slice(0, room);
    if (!toDo.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of toDo) {
        const res = await base44.integrations.Core.UploadFile({ file: f });
        if (res?.file_url) urls.push(res.file_url);
      }
      setImages(p => [...p, ...urls]);
    } catch (err) {
      setError("Image upload failed");
    }
    setUploading(false);
    e.target.value = "";
  };

  const removeImage = (i) => setImages(p => p.filter((_, idx) => idx !== i));
  const moveImage = (i, dir) => setImages(p => {
    const j = i + dir;
    if (j < 0 || j >= p.length) return p;
    const copy = [...p];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("Name is required"); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        location: form.location.trim(),
        price: form.price === "" ? null : Number(form.price),
        guests: form.guests === "" ? null : Number(form.guests),
        dateLaunched: form.dateLaunched || null,
        listingUrl: form.listingUrl.trim() || null,
        featured: !!form.featured,
        images,
      };
      if (isEdit) await base44.entities.Listing.update(listing.id, payload);
      else await base44.entities.Listing.create(payload);
      onSaved();
    } catch (err) {
      setError(err?.message || "Save failed");
    }
    setSaving(false);
  };

  return createPortal(
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }} style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", overflowY: "auto" }}>
      <div style={{ width: "100%", maxWidth: 560, background: "#fff", borderRadius: 16, boxShadow: "0 24px 80px rgba(0,0,0,0.3)", position: "relative" }}>
        <button onClick={onClose} style={{ position: "absolute", top: 14, right: 14, background: "#F5F5F5", border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><X size={16} color="#888" /></button>
        <form onSubmit={submit} style={{ padding: "32px 32px 28px" }}>
          <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 22, color: "#1A1A1A", margin: "0 0 20px" }}>{isEdit ? "Edit listing" : "Add listing"}</h2>
          {error && <div style={{ background: "#E8EDFF", color: "#4A6DC7", borderRadius: 8, padding: "10px 12px", fontSize: 13, marginBottom: 16, fontFamily: "'Inter', sans-serif" }}>{error}</div>}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label style={labelStyle}>Name *</label>
              <input style={inputStyle} value={form.name} onChange={setF("name")} placeholder="Stylish city apartment" />
            </div>
            <div>
              <label style={labelStyle}>Location</label>
              <input style={inputStyle} value={form.location} onChange={setF("location")} placeholder="Manchester" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={labelStyle}>From price (£/night)</label>
                <input style={inputStyle} type="number" min="0" value={form.price} onChange={setF("price")} placeholder="145" />
              </div>
              <div>
                <label style={labelStyle}>Guests</label>
                <input style={inputStyle} type="number" min="1" value={form.guests} onChange={setF("guests")} placeholder="4" />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Date launched</label>
              <input style={inputStyle} type="date" value={form.dateLaunched} onChange={setF("dateLaunched")} />
            </div>
            <div>
              <label style={labelStyle}>Listing URL</label>
              <input style={inputStyle} value={form.listingUrl} onChange={setF("listingUrl")} placeholder="https://www.airbnb.co.uk/rooms/…" />
            </div>
            <div onClick={() => setForm(p => ({ ...p, featured: !p.featured }))} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" }}>
              <div style={{ width: 18, height: 18, borderRadius: 5, border: "1.5px solid " + (form.featured ? "#1A1A1A" : "#ccc"), background: form.featured ? "#1A1A1A" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {form.featured && <Star size={11} color="#fff" fill="#fff" />}
              </div>
              <span style={{ fontSize: 14, color: "#333", fontFamily: "'Inter', sans-serif" }}>Feature on homepage</span>
            </div>
            <div>
              <label style={labelStyle}>Images ({images.length}/5)</label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8 }}>
                {images.map((url, i) => (
                  <div key={i} style={{ position: "relative", aspectRatio: "1", borderRadius: 8, overflow: "hidden", border: "1px solid #eee" }}>
                    <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    <span style={{ position: "absolute", top: 3, left: 3, background: "rgba(0,0,0,0.6)", color: "#fff", borderRadius: "50%", width: 20, height: 20, fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Inter', sans-serif" }}>{i + 1}</span>
                    <button type="button" onClick={() => removeImage(i)} style={{ position: "absolute", top: 3, right: 3, background: "rgba(0,0,0,0.6)", border: "none", borderRadius: "50%", width: 20, height: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Trash2 size={11} color="#fff" /></button>
                    {images.length > 1 && (
                      <div style={{ position: "absolute", bottom: 3, left: 3, right: 3, display: "flex", justifyContent: "space-between" }}>
                        <button type="button" onClick={() => moveImage(i, -1)} disabled={i === 0} style={{ background: "rgba(0,0,0,0.6)", border: "none", borderRadius: "50%", width: 20, height: 20, cursor: i === 0 ? "default" : "pointer", opacity: i === 0 ? 0.35 : 1, display: "flex", alignItems: "center", justifyContent: "center" }}><ChevronLeft size={11} color="#fff" /></button>
                        <button type="button" onClick={() => moveImage(i, 1)} disabled={i === images.length - 1} style={{ background: "rgba(0,0,0,0.6)", border: "none", borderRadius: "50%", width: 20, height: 20, cursor: i === images.length - 1 ? "default" : "pointer", opacity: i === images.length - 1 ? 0.35 : 1, display: "flex", alignItems: "center", justifyContent: "center" }}><ChevronRight size={11} color="#fff" /></button>
                      </div>
                    )}
                  </div>
                ))}
                {images.length < 5 && (
                  <label style={{ aspectRatio: "1", borderRadius: 8, border: "1.5px dashed #ccc", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#999" }}>
                    {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                    <input type="file" accept="image/*" multiple onChange={onFiles} style={{ display: "none" }} />
                  </label>
                )}
              </div>
            </div>
            <button type="submit" disabled={saving || uploading} style={{ marginTop: 6, background: "#112215", color: "#fff", border: "none", borderRadius: 10, padding: "14px", fontSize: 15, fontWeight: 700, fontFamily: "'Inter', sans-serif", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              {saving ? "Saving…" : (isEdit ? "Save changes" : "Add listing")}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}